import { lazy, Suspense, useState } from "react";
import { useAtomValue } from "jotai";

import { CreatorOnboardingWizard } from "./creator-onboarding/CreatorOnboardingWizard";
import { MobileShell } from "./render/MobileShell";
import { MobileKioskBlock } from "./render/MobileKioskBlock";
import { DemenagementVersCyrano } from "./render/DemenagementVersCyrano";
import { doitDemenager } from "./render/demenagementCyrano";
import { DemenagementVersRishfans } from "./render/DemenagementVersRishfans";
import { doitPartirSurRishfans } from "./render/demenagementRishfans";
import { MARQUE } from "@dozka/ui";
import { kioskRoleFor } from "./render/kioskRoles";
import { SondeDuPosteDeChatteur } from "@/panels/conversations/posteDeChatteur";
import { resolveMobileGate } from "./render/mobileAccessGate";
import { LayoutPickerModal } from "./LayoutPickerModal";
import { PanelPicker } from "./PanelPicker";
import { PendingInvitationsBanner } from "./PendingInvitationsBanner";
import { useKeyboardShortcuts } from "./keyboard";
import {
  kioskLayoutOverrideAtom,
  useKioskLayout,
  usePresetMetadataMigration,
  wizardLayoutOverrideAtom,
} from "./workspaces";
import { PerfHUD } from "@/core/components";
import { surUnTelephone, useIsMobile } from "@/core/hooks";
import { usePermissions } from "@/core/auth/usePermissions";
import { ConversationLockController } from "@/panels/conversations/ConversationLockController";
import { IdleReleaseWarning } from "@/panels/conversations/IdleReleaseWarning";
import { DemoBridge } from "./DemoBridge";
import { useChatterPresence } from "./presence/useChatterPresence";

const DesktopShellSurface = lazy(() => import("./DesktopShellSurface"));

// Hauteurs reservees a la wizard chrome (slim mode) — header (titre +
// stepCounter + Stepper + padding) et footer (boutons + padding). Le canvas
// se reserve ces espaces via padding-top/bottom pour que le contenu des
// panels (filtres, save button du VaultItemPanel) reste visible sous la
// chrome wizard absolute-positionnee.
const WIZARD_SLIM_HEADER_PX = 80;
const WIZARD_SLIM_FOOTER_PX = 56;

/**
 * Cockpit shell — la zone centrale du frame applicatif. TitleBar et BottomBar
 * vivent au-dessus dans `App.GatedShell` pour rester visibles dans tous les
 * etats auth (login / org onboarding / creator onboarding / cockpit normal).
 *
 *   ┌──────────────────────────────────────────────────────────┐
 *   │  cockpit-canvas (gutter padding, neutral bg)              │
 *   │  ┌─────────────────────────────────────────────────────┐ │
 *   │  │ LayoutRenderer (recursive Split / TabGroup tree)    │ │
 *   │  │  enveloppe par DndProvider pour drag-dock           │ │
 *   │  └─────────────────────────────────────────────────────┘ │
 *   └──────────────────────────────────────────────────────────┘
 *
 * `CreatorOnboardingWizard` est rendu en overlay `absolute inset-0 z-[200]`
 * (bounded a cette zone, plus a tout le viewport — TitleBar/BottomBar
 * restent visibles dessus).
 */
export function CockpitShell() {
  useKeyboardShortcuts();
  usePresetMetadataMigration();
  const wizardSlimActive = useAtomValue(wizardLayoutOverrideAtom) !== null;
  const isMobile = useIsMobile();
  const { permissions, roleSlug, isLoading: permsLoading } = usePermissions();
  const kioskRole = kioskRoleFor(roleSlug, permissions);
  const inKiosk = kioskRole !== null;

  // Rôle en kiosque : injecte son layout FIXE (cf `KIOSK_TABS`) comme override
  // in-memory, rendu par le VRAI shell (`DesktopShellSurface` →
  // `LayoutRenderer`) — mêmes panneaux qu'un rôle libre, verrouillés (cf le
  // lock de `TabStrip` + BottomBar/SideRail/keyboard). `kioskReady` évite de
  // flasher le workspace vide le temps que l'effet pose l'override.
  useKioskLayout({ kioskRole, ready: !permsLoading });
  const kioskReady = useAtomValue(kioskLayoutOverrideAtom) !== null;

  // Qui a une session de shift en cours bat vers `POST /api/presence` — au
  // niveau du shell, pas d'un panneau : la présence ne dépend pas de l'onglet
  // ouvert. Pas de condition de rôle ici : le shift gate seul décide (cf
  // `presence/useChatterPresence.ts`, et pourquoi le rôle n'est pas la bonne
  // question).
  useChatterPresence();

  // Le chatteur qui ouvre dozka.app après la bascule (14-10-2026, 8h Paris) :
  // son espace est sur cyrano.work, on l'y emmène. L'heure lue est celle de
  // l'OUVERTURE du shell — un shift en cours n'est pas coupé.
  const [ouvertA] = useState(() => Date.now());
  if (doitDemenager({ hostname: window.location.hostname, kioskRole, ouvertA })) {
    return <DemenagementVersCyrano />;
  }

  // L'affilié (14-10-2026) : son espace est app.rishfans.com, on l'y emmène —
  // il s'y reconnecte. Marko (`collaborateur`) reste : cf `demenagementRishfans.ts`.
  if (
    doitPartirSurRishfans({
      kioskRole,
      surRishfans: MARQUE.id === "rishfans",
      enDev: import.meta.env.DEV,
    })
  ) {
    return <DemenagementVersRishfans />;
  }

  // Le cockpit d'un chatter est desktop-only : sur vrai mobile, on remplace
  // tout le shell par un écran « va sur ton ordinateur ». Les autres rôles
  // gardent l'accès mobile. Pendant le chargement des permissions on rend un
  // canvas neutre pour ne PAS flasher les conversations à un chatter avant de
  // connaître son rôle. (Logique pure + testée dans mobileAccessGate.ts.)
  const mobileGate = resolveMobileGate({
    // ⚠️ Le VRAI appareil, pas `isMobile` (qui suit le forçage de rendu) :
    // sinon `?mobile=0` depuis un téléphone contourne le blocage kiosque.
    isMobile: surUnTelephone(),
    permsLoading,
    roleSlug,
    permissions,
  });
  if (mobileGate === "loading") {
    return <div className="h-full w-full bg-canvas" />;
  }
  if (mobileGate === "block") {
    return <MobileKioskBlock />;
  }

  const canvas = (
    <main
      className="cockpit-canvas relative"
      style={
        wizardSlimActive
          ? {
              paddingTop: WIZARD_SLIM_HEADER_PX,
              paddingBottom: WIZARD_SLIM_FOOTER_PX,
            }
          : undefined
      }
    >
      {isMobile ? (
        // 🪤 Le MÊME anti-flash que la branche desktop juste en dessous, et il
        // n'a commencé à compter que le 28-08 : tant qu'aucun kiosque
        // n'atteignait le mobile, `MobileShell` ne pouvait pas y afficher un
        // layout qui n'est pas le sien. Depuis que le chatteur passe, monter
        // avant que `useKioskLayout` ait posé son override lui montrerait le
        // workspace persisté — c'est-à-dire les panneaux de quelqu'un d'autre.
        inKiosk && !kioskReady ? (
          <div className="h-full w-full bg-canvas" />
        ) : (
          <MobileShell />
        )
      ) : permsLoading || (inKiosk && !kioskReady) ? (
        // Rôle encore inconnu (anti-flash : ne JAMAIS montrer le Bureau vide à
        // un éventuel rôle en kiosque avant de le connaître), ou kiosque dont
        // l'override n'est pas encore posé → canvas neutre. Sur desktop,
        // `resolveMobileGate` renvoie « allow » sans attendre les permissions,
        // donc c'est ici qu'on gère le trou de chargement.
        <div className="h-full w-full bg-canvas" />
      ) : (
        <Suspense fallback={<div className="h-full w-full bg-canvas" />}>
          <DesktopShellSurface />
        </Suspense>
      )}
    </main>
  );

  return (
    <div className="relative flex h-full w-full flex-col">
      {/* Bannière in-app : invitations en attente pour l'email du user
          authentifié, tous accounts confondus. Se cache d'elle-même si
          aucune invitation pending. Dismissible pour la session courante.
          Cf Lot 4 RBAC refacto (2026-05-27). */}
      <PendingInvitationsBanner variant="compact" />
      {/* Chatteur qui n'a écrit à personne depuis 10 min : compte à rebours
          avant la libération de ses discussions. Rend null sinon. */}
      <IdleReleaseWarning />
      {canvas}
      <PerfHUD />
      {/* Pilote le cycle de vie des verrous de conversation (acquire/heartbeat/
          release selon les onglets fan ouverts). Rend null. */}
      <ConversationLockController />
      {/* 🎯 « Est-il au poste de chatteur ? » — mesuré UNE fois ici, lu
          partout par l'atome (`posteDeChatteurAtom`). Rend null. */}
      <SondeDuPosteDeChatteur />
      {/* Mode démo : verse les vraies créatrices dans le monde de
          démonstration. Rend null hors démo. Cf `core/demo/`. */}
      <DemoBridge />
      {/* Escape hatches du layout (add panel / 12-layouts picker) — cachés
          en kiosque : le layout est verrouillé, il n'y a rien à modifier
          (override fixe + lock de TabStrip). */}
      {!inKiosk && (
        <>
          <PanelPicker />
          <LayoutPickerModal />
        </>
      )}
      <CreatorOnboardingWizard />
    </div>
  );
}
